import { CancelRounded, Add, Remove, ArrowForward } from "@mui/icons-material";
import { Grid, Tooltip, IconButton } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import {
    LLMExecutorStepOutputAction,
    LLMExecutorStepOutputActionType,
    ConditionPrimitive,
} from "models/LLMs";
import React, { createContext, useContext, useState } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { useLogicChainConfigurationBuilder } from "../LLMConfigurationBuilderContext";
import { StepOutputVariableSelector } from "./StepOutputVariableSelector";
import { StepSelector } from "./StepSelector";
import { LightGrayBackground } from "../LightGrayBackground";
import { Condition } from "./Conditions/Condition";
import { FullWidthGrid } from "../FullWidthGrid";
import { useCurrentStep } from "./Step";
import { LLMConstants } from "models/LLMs/constants";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { ResultType } from "models/ClassifierModel";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";
import { ExtendedMetadataDialog } from "components/Admin/Organizations/OrganizationDetail/OrganizationMetadataManagement/ExtendedMetadataDialog";

interface LogicChainStepActionProps {
    action: LLMExecutorStepOutputAction;
}

const executeStepOption = {
    label: "Execute Step",
    value: LLMExecutorStepOutputActionType.ExecuteStep,
};

const writeClassifierResultOption = {
    label: "Write Result",
    value: LLMExecutorStepOutputActionType.WriteClassifierResult,
};

const writeExtendedMetadataOption = {
    label: "Write to Extended Metadata",
    value: LLMExecutorStepOutputActionType.WriteExtendedMetadata,
};

const StepActionContext = createContext<LLMExecutorStepOutputAction | null>(
    null,
);
export function useCurrentStepAction() {
    const action = useContext(StepActionContext);
    if (!action) throw new Error("Invalid hook usage. No step action.");
    return action;
}

export const StepAction = observer(function (props: LogicChainStepActionProps) {
    const currentStep = useCurrentStep();
    const store = useStore(LLMClassifierBuilderStore);

    let availableThenActionConfigurationOptions = [
        executeStepOption,
        writeClassifierResultOption,
        writeExtendedMetadataOption,
    ];

    // seems like this shouldn't live here but ...
    if (
        store.classifier.implementation.llmPurpose ===
        LLMClassifierPurpose.ExtractData
    ) {
        availableThenActionConfigurationOptions = [
            executeStepOption,
            writeExtendedMetadataOption,
        ];
    }

    return (
        <StepActionContext.Provider value={props.action}>
            <Grid
                container
                item
                columnGap={1}
                alignItems="start"
                flexWrap="nowrap"
            >
                <Grid
                    container
                    item
                    direction="column"
                    rowGap={1}
                    flexWrap="nowrap"
                >
                    <Grid
                        container
                        item
                        columnGap={1}
                        flexWrap="nowrap"
                        alignItems="center"
                    >
                        <Grid item>
                            <AcxChip
                                color="lightBlue"
                                label="IF"
                                size="small"
                            />
                        </Grid>
                        <LightGrayBackground
                            container
                            item
                            direction="column"
                            flexWrap="nowrap"
                            rowGap={1}
                        >
                            {props.action.conditions.map((condition, idx) => (
                                <React.Fragment key={condition.id}>
                                    {idx > 0 && (
                                        <Grid item>
                                            <AcxChip
                                                color="lightBlue"
                                                label="AND"
                                                size="small"
                                            />
                                        </Grid>
                                    )}
                                    <Condition condition={condition} />
                                </React.Fragment>
                            ))}
                            <AcxButton
                                color="white"
                                sx={(theme) => ({
                                    color: theme.palette.primary.main,
                                })}
                                startIcon={<Add />}
                                onClick={() =>
                                    props.action.addCondition(
                                        new ConditionPrimitive(),
                                    )
                                }
                            >
                                And
                            </AcxButton>
                        </LightGrayBackground>
                    </Grid>
                    <Grid
                        container
                        item
                        columnGap={1}
                        flexWrap="nowrap"
                        alignItems="center"
                    >
                        <Grid item>
                            <AcxChip
                                color="lightBlue"
                                label="THEN"
                                size="small"
                            />
                        </Grid>
                        <LightGrayBackground
                            container
                            item
                            direction="column"
                            flexWrap="nowrap"
                            rowGap={1}
                        >
                            <Grid
                                container
                                item
                                justifyContent="space-evenly"
                                flexWrap="nowrap"
                                alignItems="center"
                                columnGap={1}
                            >
                                <FullWidthGrid item>
                                    <AcxSelect
                                        id=""
                                        fullWidth
                                        labelField="label"
                                        valueField="value"
                                        options={
                                            availableThenActionConfigurationOptions
                                        }
                                        defaultValue={availableThenActionConfigurationOptions.find(
                                            (option) =>
                                                option.value ===
                                                props.action.thenActionType,
                                        )}
                                        onChange={(option) => {
                                            props.action.setThenActionType(
                                                option.value,
                                            );
                                            if (
                                                props.action.isWriteClassifierResult()
                                            ) {
                                                props.action.setClassifierId(
                                                    store.classifier.id,
                                                );
                                            }
                                        }}
                                    />
                                </FullWidthGrid>
                                {props.action.isExecuteStep() && (
                                    <ExecuteStepActionItems />
                                )}
                                {props.action.isWriteClassifierResult() && (
                                    <WriteToClassifierResultActionItems />
                                )}
                                {props.action.isWriteToExtendedMetadata() && (
                                    <WriteToExtendedMetadataActionItems />
                                )}
                            </Grid>
                            {props.action.isExecuteStep() &&
                                props.action.usableInputVariables.map(
                                    (inputVariable, idx) => (
                                        <Grid
                                            container
                                            item
                                            justifyContent="space-evenly"
                                            flexWrap="nowrap"
                                            columnGap={1}
                                            key={idx}
                                        >
                                            <FullWidthGrid item>
                                                <AcxMainTextField
                                                    id=""
                                                    fullWidth
                                                    value={
                                                        inputVariable.sourceVariable
                                                    }
                                                    onChange={(event) => {
                                                        const value =
                                                            event.target.value;
                                                        props.action.setInputVariableSource(
                                                            inputVariable,
                                                            value,
                                                        );
                                                    }}
                                                />
                                            </FullWidthGrid>
                                            <FullWidthGrid item>
                                                <AcxMainTextField
                                                    id=""
                                                    fullWidth
                                                    value={
                                                        inputVariable.targetVariable
                                                    }
                                                    onChange={(event) => {
                                                        const value =
                                                            event.target.value;
                                                        props.action.setInputVariableTarget(
                                                            inputVariable,
                                                            value,
                                                        );
                                                    }}
                                                />
                                            </FullWidthGrid>
                                            <Grid
                                                item
                                                minWidth="140px"
                                                justifyContent="flex-start"
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        props.action.setInputVariableTarget(
                                                            inputVariable,
                                                            undefined,
                                                        )
                                                    }
                                                >
                                                    <Remove fontSize="small" />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    ),
                                )}
                        </LightGrayBackground>
                    </Grid>
                </Grid>
                <Grid item>
                    <Tooltip title="Remove action">
                        <IconButton
                            size="small"
                            onClick={() =>
                                currentStep.configuration.removeAction(
                                    props.action,
                                )
                            }
                        >
                            <CancelRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
        </StepActionContext.Provider>
    );
});

const createNewStepId = "__createNewStep";

const ExecuteStepActionItems = observer(function () {
    const builder = useLogicChainConfigurationBuilder();
    const currentStep = useCurrentStep();
    const currentAction = useCurrentStepAction();

    const steps = builder.steps
        .filter((step) => step.id !== currentStep.id)
        .map<{ id: string; name: React.ReactNode }>((step) => ({
            id: step.id,
            name: step.name,
        }))
        .concat([
            {
                id: createNewStepId,
                name: (
                    <Grid container alignItems="center" columnGap={1}>
                        <Add fontSize="small" />
                        <Grid item>Step ({builder.getNextStepName()})</Grid>
                    </Grid>
                ),
            },
        ]);

    const selectedStep = steps.find(
        (step) => step.id === currentAction.thenActionConfiguration["stepId"],
    );

    return (
        <>
            <FullWidthGrid item>
                <AcxSelectSingle
                    id={currentStep.id}
                    fullWidth
                    labelField="name"
                    valueField="id"
                    placeholder="Select step to execute..."
                    options={steps}
                    defaultValue={selectedStep}
                    onChange={(step) => {
                        if (step.id === createNewStepId) {
                            const newStep = builder.addStep();
                            currentAction.setStepId(newStep.id);
                        } else {
                            currentAction.setStepId(step.id);
                        }
                    }}
                />
            </FullWidthGrid>
            <Grid item minWidth="140px">
                <AcxButton
                    color="white"
                    startIcon={<Add />}
                    sx={(theme) => ({
                        color: theme.palette.primary.main,
                        backgroundColor: "transparent",
                        border: "none",
                        padding: theme.spacing(1),
                    })}
                    onClick={() => currentAction.addInputVariable()}
                >
                    Input Variable
                </AcxButton>
            </Grid>
        </>
    );
});

const WriteToClassifierResultActionItems = observer(function () {
    const builder = useLogicChainConfigurationBuilder();
    const currentStep = useCurrentStep();
    const currentAction = useCurrentStepAction();

    let stepIdForWriteToClassifierResult = currentStep.id;
    const writeFromVariable: string =
        currentAction.thenActionConfiguration["writeFromVariable"];
    if (writeFromVariable) {
        stepIdForWriteToClassifierResult = writeFromVariable.split(".")[0];
    }

    const [isStatic, setIsStatic] = useState(
        stepIdForWriteToClassifierResult.startsWith(
            LLMConstants.StaticValueVariablePrefix,
        ),
    );
    // previously we just stored a step id here. but due to the fact that renaming a step
    // will change its id, we store a reference to the step locally. trying to react to
    // changes in the step id and then updating the local state is not worth it.
    const [writeFromClassifierStep, setWriteFromClassifierStep] = useState(
        builder.steps.find(
            (step) => step.id === stepIdForWriteToClassifierResult,
        ) ?? currentStep,
    );

    return (
        <React.Fragment>
            <FullWidthGrid item>
                <StepSelector
                    selectedStepId={
                        isStatic
                            ? LLMConstants.StaticValueVariablePrefix
                            : writeFromClassifierStep.id
                    }
                    thisStepId={currentStep.id}
                    steps={builder.steps}
                    onChange={(step, isStatic) => {
                        setIsStatic(isStatic);
                        if (step) {
                            setWriteFromClassifierStep(step);
                        }

                        currentAction.setWriteFromVariable("");
                        currentAction.setShortWriteFromVariable("");
                    }}
                />
            </FullWidthGrid>
            <FullWidthGrid item>
                {isStatic ? (
                    <AcxMainTextField
                        id=""
                        fullWidth
                        placeholderText="Enter value to write..."
                        value={
                            currentAction.thenActionConfiguration[
                                "shortWriteFromVariable"
                            ]
                        }
                        onChange={(event) => {
                            const value = event.target.value;
                            currentAction.setShortWriteFromVariable(value);
                            currentAction.setWriteFromVariable(
                                LLMConstants.StaticValueVariablePrefix +
                                    "." +
                                    value,
                            );
                        }}
                    />
                ) : (
                    <StepOutputVariableSelector
                        step={writeFromClassifierStep}
                        variable={
                            currentAction.thenActionConfiguration[
                                "shortWriteFromVariable"
                            ]
                        }
                        onChange={(variable) => {
                            currentAction.setShortWriteFromVariable(variable);
                            currentAction.setWriteFromVariable(
                                // ensures we get the correct {stepId}.{propertyName} format based
                                // on the user selected step
                                writeFromClassifierStep.getPropertyName(
                                    variable,
                                ),
                            );
                        }}
                    />
                )}
            </FullWidthGrid>
        </React.Fragment>
    );
});

const WriteToExtendedMetadataActionItems = observer(
    function WriteToExtendedMetadataActionItems() {
        const store = useStore(LLMClassifierBuilderStore);
        const builder = useLogicChainConfigurationBuilder();
        const currentStep = useCurrentStep();
        const currentAction = useCurrentStepAction();

        const [isCreateExtendedMetadataOpen, setIsCreateExtendedMetadataOpen] =
            useState(false);

        let stepIdForWriteToExtendedMetadata = currentStep.id;
        const writeFromVariable: string =
            currentAction.thenActionConfiguration["writeFromVariable"];
        if (writeFromVariable) {
            stepIdForWriteToExtendedMetadata = writeFromVariable.split(".")[0];
        }

        const [isStatic, setIsStatic] = useState(
            stepIdForWriteToExtendedMetadata.startsWith(
                LLMConstants.StaticValueVariablePrefix,
            ),
        );
        // previously we just stored a step id here. but due to the fact that renaming a step
        // will change its id, we store a reference to the step locally. trying to react to
        // changes in the step id and then updating the local state is not worth it.
        const [
            writeFromExtendedMetadataStep,
            setWriteFromExtendedMetadataStep,
        ] = useState(
            builder.steps.find(
                (step) => step.id === stepIdForWriteToExtendedMetadata,
            ) ?? currentStep,
        );

        const extendedMetadataToUse =
            store.getExtendedMetadataToUseForStep(currentStep);

        const extendedMetadataOptions = extendedMetadataToUse.map((ext) => ({
            label: ext.name,
            value: ext.id,
        }));

        const extendedMetadataOption = extendedMetadataOptions.find(
            (option) =>
                option.value ===
                    currentAction.thenActionConfiguration[
                        "extendedMetadataClassifierId"
                    ] ||
                // this check is for backwards compatability
                option.label ===
                    currentAction.thenActionConfiguration[
                        "extendedMetadataFieldName"
                    ],
        );
        const extendedMetadataClassifier = extendedMetadataToUse.find(
            (c) => c.id === extendedMetadataOption?.value,
        );

        return (
            <React.Fragment>
                <FullWidthGrid item>
                    <StepSelector
                        selectedStepId={
                            isStatic
                                ? LLMConstants.StaticValueVariablePrefix
                                : writeFromExtendedMetadataStep.id
                        }
                        thisStepId={currentStep.id}
                        steps={builder.steps}
                        onChange={(step, isStatic) => {
                            setIsStatic(isStatic);
                            if (step) {
                                setWriteFromExtendedMetadataStep(step);
                            }

                            currentAction.setWriteFromVariable("");
                            currentAction.setShortWriteFromVariable("");
                        }}
                    />
                </FullWidthGrid>
                <FullWidthGrid item>
                    {isStatic ? (
                        <AcxMainTextField
                            id=""
                            fullWidth
                            placeholderText="Enter value to write..."
                            value={
                                currentAction.thenActionConfiguration[
                                    "shortWriteFromVariable"
                                ]
                            }
                            onChange={(event) => {
                                const value = event.target.value;
                                currentAction.setShortWriteFromVariable(value);
                                currentAction.setWriteFromVariable(
                                    LLMConstants.StaticValueVariablePrefix +
                                        "." +
                                        value,
                                );
                            }}
                        />
                    ) : (
                        <StepOutputVariableSelector
                            step={writeFromExtendedMetadataStep}
                            variable={
                                currentAction.thenActionConfiguration[
                                    "shortWriteFromVariable"
                                ]
                            }
                            onChange={(variable) => {
                                currentAction.setShortWriteFromVariable(
                                    variable,
                                );
                                currentAction.setWriteFromVariable(
                                    // ensures we get the correct {stepId}.{propertyName} format based
                                    // on the user selected step
                                    writeFromExtendedMetadataStep.getPropertyName(
                                        variable,
                                    ),
                                );
                            }}
                        />
                    )}
                </FullWidthGrid>
                <ArrowForward
                    fontSize="small"
                    sx={(theme) => ({
                        fill: theme.palette.gray[400],
                    })}
                />
                <FullWidthGrid item>
                    <AcxSelectSingle
                        id=""
                        fullWidth
                        placeholder="Select extended metadata field name..."
                        valueField="label"
                        labelField="label"
                        options={extendedMetadataOptions.sort((a, b) =>
                            a.label
                                .toLocaleLowerCase()
                                .localeCompare(b.label.toLocaleLowerCase()),
                        )}
                        defaultValue={extendedMetadataOption}
                        onChange={(option) => {
                            currentAction.setExtendedMetadataClassifierId(
                                option.value,
                            );
                        }}
                        dropdownMenuEndChildren={
                            <AcxButton
                                fullWidth
                                variant="outlined"
                                startIcon={<Add fontSize="small" />}
                                sx={(theme) => ({
                                    borderRadius: "0px",
                                    border: "none",
                                    color: theme.palette.gray[700],
                                    backgroundColor: theme.palette.white.main,
                                    textAlign: "start",
                                    justifyContent: "start",
                                    fontWeight: "semibold",
                                    "&:hover": {
                                        border: "none",
                                        color: theme.palette.gray[700],
                                        backgroundColor:
                                            theme.palette.gray[100],
                                    },
                                })}
                                onClick={() =>
                                    setIsCreateExtendedMetadataOpen(true)
                                }
                            >
                                Add Extended Metadata
                            </AcxButton>
                        }
                    />
                    <ExtendedMetadataDialog
                        open={isCreateExtendedMetadataOpen}
                        onClose={() => setIsCreateExtendedMetadataOpen(false)}
                        existingMetadata={store.allExtendedMetadata}
                        onSave={(field) => {
                            store
                                .getExtendedMetadataToUseForStep(currentStep)
                                .push(field);
                            currentAction.setExtendedMetadataClassifierId(
                                field.id,
                            );
                        }}
                    />
                </FullWidthGrid>
                {currentStep.configuration.useStructuredOutputs &&
                    extendedMetadataClassifier?.resultType ===
                        ResultType.JsonType &&
                    extendedMetadataClassifier.jsonSchema && (
                        <Tooltip title="Sets the structured output json schema to the json schema of the currently selected extended metadata classifier">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    currentStep.configuration.setStructuredOutputJsonSchema(
                                        extendedMetadataClassifier.jsonSchema,
                                    )
                                }
                            >
                                <DataObjectIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
            </React.Fragment>
        );
    },
);
